/**
 * The app's single dropdown. Native `<select>` renders its option list through
 * the OS, which no stylesheet can reach — the popup arrives with the platform's
 * own blue highlight and metrics, so two selects side by side never match the
 * rest of the product. This is the same listbox everywhere instead: a bordered
 * white panel (no shadow — DESIGN-SYSTEM.md Elevation) whose current value carries a
 * right-aligned check.
 *
 * Three triggers, one popup:
 * - `boxed` — the ordinary form control, styled with the shared field rules
 *   (`components/ui/field.module.css`).
 * - `quiet` — text + chevron, no box. Settings rows (DESIGN-SYSTEM.md "Selects are
 *   quiet") and the studio preview switchers.
 * - `cell` — the inline-edit treatment: full width, transparent border that
 *   appears on hover to mark the value as editable (DESIGN-SYSTEM.md Table inputs).
 * - `badge` — a status pill that is also a picker (the event phase override).
 *   It carries no colour of its own: the caller passes the semantic bg/text so
 *   the trigger reads as the badge it replaces.
 *
 * Keyboard parity with the native control: Up/Down/Home/End move the highlight,
 * Enter or Space commits, Escape and outside click close, Tab closes without
 * committing, and focus returns to the trigger on close.
 *
 * NOT for table cells. The popup is absolutely positioned, so any `overflow-*`
 * ancestor clips it for the bottom rows — the invariant DESIGN.md already
 * records for `EquipmentAutocomplete`. Selects inside `ResponsiveTable` stay
 * native until this grows anchor positioning.
 */
import { useEffect, useId, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "./icons";
import { cx } from "@shared/cx";
import formStyles from "./field.module.css";
import cellStyles from "./cell-input.module.css";
import { useDismissable } from "../../lib/use-dismissable";
import styles from "./Select.module.css";

export type SelectOption = { value: string; label: string };

export function Select({
  id,
  value,
  options,
  onChange,
  variant = "boxed",
  size = "md",
  disabled = false,
  className = "",
  invalid = false,
  ariaLabel,
}: {
  id?: string;
  value: string;
  options: SelectOption[];
  onChange: (next: string) => void;
  variant?: "boxed" | "quiet" | "cell" | "badge";
  /** `boxed` only: `sm` is the filter-bar height (the compact field). */
  size?: "md" | "sm";
  disabled?: boolean;
  /** Extra classes for the trigger (width, badge colours). */
  className?: string;
  invalid?: boolean;
  ariaLabel?: string;
}) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listboxId = `${baseId}-listbox`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selectedLabel =
    selectedIndex >= 0 ? options[selectedIndex].label : value;

  // Keep the highlighted option in view when the list is long enough to scroll.
  useEffect(() => {
    if (!open) return;
    document
      .getElementById(`${baseId}-option-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, baseId]);

  const openList = () => {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Outside click closes without refocus (the user clicked elsewhere on
  // purpose); Escape closes and returns focus to the trigger.
  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    onEscape: close,
    pointer: [containerRef],
  });

  // Picking an option IS the commit; an unchanged value never fires a change.
  const commit = (next: string) => {
    close();
    if (next !== value) onChange(next);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case "Enter":
      case " ": {
        e.preventDefault();
        const active = options[activeIndex];
        if (active) commit(active.value);
        break;
      }
      // Escape is handled by useDismissable, which also covers the case
      // where focus has left the trigger.
      case "Tab":
        setOpen(false);
        break;
    }
  };

  // `quiet` and `badge` size to their label; the other two fill their field.
  const full = variant === "boxed" || variant === "cell";
  const triggerClass = {
    boxed: cx(
      size === "sm"
        ? cx(formStyles.inputCompact, styles.triggerCompactFull)
        : formStyles.input,
      styles.triggerBoxed,
      invalid && styles.invalid,
    ),
    // The look is the shared cell-input treatment; `.triggerCell` adds only
    // the label/chevron row. The two set different properties, so nothing
    // depends on which stylesheet the bundler emitted last.
    cell: cx(cellStyles.cellDense, styles.triggerCell, invalid && styles.invalid),
    quiet: styles.triggerQuiet,
    // No colour class here: the caller's semantic bg/text would collide with
    // one, and CSS Modules do not merge — the winner would be CSS source
    // order rather than intent. See `.triggerBadge` in the module.
    badge: styles.triggerBadge,
  }[variant];

  return (
    <span
      ref={containerRef}
      className={full ? styles.containerFull : styles.containerInline}
    >
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={
          open ? `${baseId}-option-${activeIndex}` : undefined
        }
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onTriggerKeyDown}
        className={cx(styles.trigger, triggerClass, className)}
      >
        {/* A zero-width space holds the line box when no option is selected,
            so a boxed trigger keeps an input's height. */}
        <span className={styles.label}>{selectedLabel || "​"}</span>
        <ChevronDownIcon
          className={
            variant === "badge"
              ? styles.chevronBadge
              : cx(
                  styles.chevron,
                  full ? styles.chevronField : styles.chevronBare,
                )
          }
        />
      </button>
      {open && (
        <span
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          className={cx(
            styles.listbox,
            full ? styles.listboxFull : styles.listboxInline,
          )}
        >
          {options.map((o, i) => (
            <button
              key={o.value}
              id={`${baseId}-option-${i}`}
              type="button"
              role="option"
              aria-selected={o.value === value}
              tabIndex={-1}
              onClick={() => commit(o.value)}
              className={cx(
                styles.option,
                i === activeIndex && styles.optionActive,
              )}
            >
              <span
                className={full ? styles.optionLabel : styles.optionLabelInline}
              >
                {o.label}
              </span>
              {o.value === value && <CheckIcon className={styles.check} />}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
